import { Check, BarChart3, TrendingUp, DollarSign } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminRevenue = ({ revenueStats, commerceTransactions, getAuthHeaders, fetchData }) => (
  <section className="px-6 py-6">
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { key: "total", icon: DollarSign, label: "Total Transactions", value: revenueStats?.total_transactions || 0, color: "#1A1915" },
          { key: "completed", icon: Check, label: "Completed", value: revenueStats?.completed || 0, color: "#2C4A3B" },
          { key: "fees", icon: TrendingUp, label: "Fee Revenue", value: `$${(revenueStats?.revenue?.total_fees || 0).toLocaleString()}`, color: "#D4A017" },
          { key: "gross", icon: BarChart3, label: "Gross Volume", value: `$${(revenueStats?.revenue?.total_gross || 0).toLocaleString()}`, color: "#1A1915" },
        ].map(({ key, icon: Icon, label, value, color }) => (
          <div key={key} className="bg-white border-2 border-[#D1CFC5] rounded-xl p-5" data-testid={`revenue-stat-${key}`}>
            <div className="flex items-center gap-2 text-[#5C5A52] mb-1"><Icon className="w-4 h-4" /><span className="text-xs">{label}</span></div>
            <p className="text-3xl font-bold" style={{ color }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Revenue by Category + Tier Distribution */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white border-2 border-[#D1CFC5] rounded-xl p-5">
          <h3 className="font-heading text-lg font-semibold text-[#1A1915] mb-4">Revenue by Category</h3>
          {(revenueStats?.by_category || []).length > 0 ? (
            <div className="space-y-3">
              {revenueStats.by_category.map((cat, i) => (
                <div key={cat.category || `cat-${i}`} className="flex items-center justify-between border-b border-[#EFEDE5] pb-2">
                  <div>
                    <p className="font-medium text-[#1A1915] text-sm capitalize">{cat.category || "Unknown"}</p>
                    <p className="text-xs text-[#5C5A52]">{cat.count} transaction{cat.count !== 1 ? "s" : ""}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-[#1A1915]">${(cat.gross || 0).toLocaleString()}</p>
                    <p className="text-xs text-[#D4A017]">Fee: ${(cat.fees || 0).toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-[#5C5A52]">No transactions yet.</p>}
        </div>

        <div className="bg-white border-2 border-[#D1CFC5] rounded-xl p-5">
          <h3 className="font-heading text-lg font-semibold text-[#1A1915] mb-4">User Tier Distribution</h3>
          {(revenueStats?.tier_distribution || []).length > 0 ? (
            <div className="space-y-3">
              {revenueStats.tier_distribution.map((td, i) => {
                const colors = { free: "#5C5A52", founding: "#14B8A6", professional: "#2C4A3B", featured: "#D4A017" };
                return (
                  <div key={td.tier || `tier-${i}`} className="flex items-center justify-between">
                    <span className="text-sm font-medium capitalize" style={{ color: colors[td.tier] || "#1A1915" }}>{td.tier || "Unknown"}</span>
                    <span className="text-sm font-bold text-[#1A1915]">{td.count} user{td.count !== 1 ? "s" : ""}</span>
                  </div>
                );
              })}
            </div>
          ) : <p className="text-sm text-[#5C5A52]">No users registered yet.</p>}
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white border-2 border-[#D1CFC5] rounded-xl overflow-hidden">
        <div className="p-4 border-b border-[#D1CFC5] flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold text-[#1A1915]">Commerce Transactions</h3>
          <span className="text-xs text-[#5C5A52]">{commerceTransactions.length} total</span>
        </div>
        {commerceTransactions.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Business</TableHead>
                <TableHead>Gross</TableHead>
                <TableHead>Fee</TableHead>
                <TableHead>Net</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {commerceTransactions.map(txn => (
                <TableRow key={txn.id}>
                  <TableCell>
                    <p className="font-medium text-[#1A1915] text-sm">{txn.business_name}</p>
                    <p className="text-xs text-[#5C5A52]">Ref: {txn.id?.slice(0, 8)}</p>
                  </TableCell>
                  <TableCell className="font-semibold">${txn.gross_amount?.toLocaleString()}</TableCell>
                  <TableCell className="text-[#D4A017]">${txn.fee_amount?.toFixed(2)} ({txn.fee_percent}%)</TableCell>
                  <TableCell className="text-[#2C4A3B]">${txn.net_to_provider?.toLocaleString()}</TableCell>
                  <TableCell className="text-sm capitalize">{txn.payment_method}</TableCell>
                  <TableCell>
                    <Badge className={txn.status === "completed" ? "bg-[#2C4A3B] text-white" : "bg-[#D4A017]/20 text-[#D4A017]"}>{txn.status}</Badge>
                  </TableCell>
                  <TableCell>
                    {txn.status !== "completed" && (
                      <Button size="sm" className="bg-[#2C4A3B] text-white text-xs h-7"
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/commerce/admin/transactions/${txn.id}/complete`, {}, getAuthHeaders());
                            toast.success("Transaction completed");
                            fetchData();
                          } catch { /* ignore */ }
                        }}
                        data-testid={`complete-txn-${txn.id}`}
                      >Mark Complete</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="p-8 text-center text-[#5C5A52]">No commerce transactions yet.</div>
        )}
      </div>
    </div>
  </section>
);
